import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { PlusCircle, X, ChevronDown, Box, Trash2 } from "feather-icons-react/build/IconComponents";
import ImageWithBasePath from "../../core/img/imagewithbasebath";
import {
  deleteBrand,
  createCallCenter,
  updateCallCenter,
  deleteCallCenter,
  fetchCallCentersByBrand,
  createPhoneNumber,
  updatePhoneNumber,
  deletePhoneNumber,
  fetchPhoneNumbersByCallCenter,
  getAllBrands,
} from "../../core/api/settings";
import { getAllPartners, createPartner } from "../../feature-module/Accounting/api/accounting";
import Swal from "sweetalert2";

const BrandForm = ({
  brandData,
  index,
  onRemoveNestedField,
  onCancelBrand,
  onSaveBrand,
  setClientData,
  fetchClients,
}) => {
  const [callCenters, setCallCenters] = useState([]);
  const [phoneNumbers, setPhoneNumbers] = useState([]);
  const [newCallCenter, setNewCallCenter] = useState("");
  const [newPhoneNumber, setNewPhoneNumber] = useState("");
  const [selectedCallCenterForPhone, setSelectedCallCenterForPhone] = useState("");
  const [phoneStatus, setPhoneStatus] = useState("Available");
  
  // Partner copying state
  const [useExistingPartners, setUseExistingPartners] = useState(false);
  const [availableBrands, setAvailableBrands] = useState([]);
  const [sourceBrandId, setSourceBrandId] = useState("");
  const [sourcePartners, setSourcePartners] = useState([]);
  const [selectedPartners, setSelectedPartners] = useState([]);
  const [loadingPartners, setLoadingPartners] = useState(false);

  // Load call centers and phone numbers when brand is available
  useEffect(() => {
    if (brandData?.id) {
      loadCallCenters();
    }
  }, [brandData?.id]);

  // Load available brands for partner copying (only for new brands)
  useEffect(() => {
    if (!brandData?.id && useExistingPartners) {
      loadAvailableBrands();
    }
  }, [useExistingPartners, brandData?.id]);

  // Load partners when source brand is selected
  useEffect(() => {
    if (sourceBrandId && useExistingPartners) {
      loadSourcePartners();
    } else {
      setSourcePartners([]);
      setSelectedPartners([]);
    }
  }, [sourceBrandId, useExistingPartners]);

  const loadAvailableBrands = async () => {
    try {
      const response = await getAllBrands();
      // Filter out the current brand if it exists
      const filteredBrands = (response.data || []).filter(
        (brand) => brand.id !== brandData?.id
      );
      setAvailableBrands(filteredBrands);
    } catch (error) {
      console.error("Error loading brands:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to load brands. Please try again.",
      });
    }
  };

  const loadSourcePartners = async () => {
    if (!sourceBrandId) return;
    
    setLoadingPartners(true);
    try {
      const response = await getAllPartners({ brand: sourceBrandId });
      // Handle different response formats
      let partnersArray = [];
      if (Array.isArray(response)) {
        partnersArray = response;
      } else if (response?.data) {
        partnersArray = Array.isArray(response.data) ? response.data : [];
      } else if (response?.results) {
        partnersArray = response.results;
      }
      
      setSourcePartners(partnersArray);
      // Pre-select all partners by default
      setSelectedPartners(partnersArray.map((p) => p.id));
    } catch (error) {
      console.error("Error loading partners:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to load partners. Please try again.",
      });
      setSourcePartners([]);
    } finally {
      setLoadingPartners(false);
    }
  };

  const handlePartnerToggle = (partnerId) => {
    setSelectedPartners((prev) => {
      if (prev.includes(partnerId)) {
        return prev.filter((id) => id !== partnerId);
      } else {
        return [...prev, partnerId];
      }
    });
  };

  const handleSelectAllPartners = () => {
    if (selectedPartners.length === sourcePartners.length) {
      setSelectedPartners([]);
    } else {
      setSelectedPartners(sourcePartners.map((p) => p.id));
    }
  };

  const loadCallCenters = async () => {
    try {
      const response = await fetchCallCentersByBrand(brandData.id);
      setCallCenters(response.data);

      // Load phone numbers for each call center
      const allPhoneNumbers = [];
      for (const callCenter of response.data) {
        try {
          const phoneResponse = await fetchPhoneNumbersByCallCenter(callCenter.id);
          allPhoneNumbers.push(...phoneResponse.data);
        } catch (error) {
          console.error(`Error loading phone numbers for call center ${callCenter.id}:`, error);
        }
      }
      setPhoneNumbers(allPhoneNumbers);
    } catch (error) {
      console.error("Error loading call centers:", error);
      setCallCenters([]);
    }
  };

  const handleAddNestedField = (brandIndex, field) => {
    setClientData((prevData) => {
      const updatedBrands = prevData.brands?.map((brand, idx) => {
        if (idx === brandIndex) {
          return {
            ...brand,
            [field]: [...brand[field], ""],
          };
        }
        return brand;
      });

      return { ...prevData, brands: updatedBrands };
    });
  };
  const handleBrandLogoChange = (brandIndex, file) => {
    setClientData((prevData) => {
      const updatedBrands = prevData.brands?.map((brand, idx) => {
        if (idx === brandIndex) {
          return { ...brand, brand_logo: file };
        }
        return brand;
      });

      return { ...prevData, brands: updatedBrands };
    });
  };

  const handleBrandFieldChange = (brandIndex, field, value) => {
    setClientData((prevData) => {
      const updatedBrands = [...prevData.brands];
      updatedBrands[brandIndex][field] = value;
      return { ...prevData, brands: updatedBrands };
    });
  };

  const handleAddLogisticInfo = (brandIndex) => {
    setClientData((prevData) => {
      const updatedBrands = prevData.brands?.map((brand, idx) => {
        if (idx === brandIndex) {
          return {
            ...brand,
            logistics: [...brand.logistics, { name: "", address: "" }],
          };
        }
        return brand;
      });
      return { ...prevData, brands: updatedBrands };
    });
  };

  const handleRemoveLogisticInfo = (brandIndex, logisticIndex) => {
    setClientData((prevData) => {
      const updatedBrands = [...prevData.brands];
      updatedBrands[brandIndex].logistics.splice(logisticIndex, 1);
      return { ...prevData, brands: updatedBrands };
    });
  };

  const handleLogisticFieldChange = (brandIndex, logisticIndex, field, value) => {
    setClientData((prevData) => {
      const updatedBrands = [...prevData.brands];
      updatedBrands[brandIndex].logistics[logisticIndex][field] = value;
      return { ...prevData, brands: updatedBrands };
    });
  };

  const handleAddMerchantAccount = (brandIndex) => {
    setClientData((prevData) => {
      const updatedBrands = prevData.brands?.map((brand, idx) => {
        if (idx === brandIndex) {
          return {
            ...brand,
            merchant_accounts: [
              ...brand.merchant_accounts,
              { account: "", gateways: [{ name: "", link: "" }] },
            ],
          };
        }
        return brand;
      });
      return { ...prevData, brands: updatedBrands };
    });
  };

  const handleRemoveMerchantAccount = (brandIndex, accountIndex) => {
    setClientData((prevData) => {
      const updatedBrands = [...prevData.brands];
      updatedBrands[brandIndex].merchant_accounts.splice(accountIndex, 1);
      return { ...prevData, brands: updatedBrands };
    });
  };

  const handleMerchantAccountFieldChange = (brandIndex, accountIndex, field, value) => {
    setClientData((prevData) => {
      const updatedBrands = [...prevData.brands];
      updatedBrands[brandIndex].merchant_accounts[accountIndex][field] = value;
      return { ...prevData, brands: updatedBrands };
    });
  };
  const handleAddGateway = (brandIndex, accountIndex) => {
    setClientData((prevData) => {
      const updatedBrands = prevData.brands?.map((brand, idx) => {
        if (idx === brandIndex) {
          const updatedAccounts = brand.merchant_accounts?.map((account, accIdx) => {
            if (accIdx === accountIndex) {
              return {
                ...account,
                gateways: [...account.gateways, { name: "", link: "" }],
              };
            }
            return account;
          });
          return { ...brand, merchant_accounts: updatedAccounts };
        }
        return brand;
      });
      return { ...prevData, brands: updatedBrands };
    });
  };

  const handleRemoveGateway = (brandIndex, accountIndex, gatewayIndex) => {
    setClientData((prevData) => {
      const updatedBrands = [...prevData.brands];
      updatedBrands[brandIndex].merchant_accounts[accountIndex].gateways.splice(gatewayIndex, 1);
      return { ...prevData, brands: updatedBrands };
    });
  };
  const handleGatewayFieldChange = (brandIndex, accountIndex, gatewayIndex, field, value) => {
    setClientData((prevData) => {
      const updatedBrands = [...prevData.brands];
      updatedBrands[brandIndex].merchant_accounts[accountIndex].gateways[gatewayIndex][field] =
        value;
      return { ...prevData, brands: updatedBrands };
    });
  };
  const handleBrandEmailChange = (brandIndex, emailIndex, value) => {
    setClientData((prevData) => {
      const updatedBrands = prevData.brands?.map((brand, idx) => {
        if (idx === brandIndex) {
          const updatedEmails = [...brand.brand_emails];
          updatedEmails[emailIndex] = value;
          return { ...brand, brand_emails: updatedEmails };
        }
        return brand;
      });

      return { ...prevData, brands: updatedBrands };
    });
  };

  const handleReturnAddressChange = (brandIndex, addressIndex, value) => {
    setClientData((prevData) => {
      const updatedBrands = prevData.brands?.map((brand, idx) => {
        if (idx === brandIndex) {
          const updatedAddresses = [...brand.return_address];
          updatedAddresses[addressIndex] = value;
          return { ...brand, return_address: updatedAddresses };
        }
        return brand;
      });

      return { ...prevData, brands: updatedBrands };
    });
  };

  const handleBankChange = (brandIndex, bankIndex, value) => {
    setClientData((prevData) => {
      const updatedBrands = prevData.brands?.map((brand, idx) => {
        if (idx === brandIndex) {
          const updatedBanks = [...brand.banks];
          updatedBanks[bankIndex] = value;
          return { ...brand, banks: updatedBanks };
        }
        return brand;
      });

      return { ...prevData, brands: updatedBrands };
    });
  };
  const handlePayeeNameChange = (brandIndex, payeeIndex, value) => {
    setClientData((prevData) => {
      const updatedBrands = prevData.brands?.map((brand, idx) => {
        if (idx === brandIndex) {
          const updatedPayees = [...brand.payee_names];
          updatedPayees[payeeIndex] = value;
          return { ...brand, payee_names: updatedPayees };
        }
        return brand;
      });

      return { ...prevData, brands: updatedBrands };
    });
  };

  // Call Center handlers
  const handleSaveCallCenter = async () => {
    if (!newCallCenter.trim()) {
      Swal.fire({
        icon: "warning",
        title: "Warning",
        text: "Please enter a call center name.",
      });
      return;
    }

    try {
      const callCenterData = {
        name: newCallCenter,
        brand: brandData.id,
      };

      const response = await createCallCenter(callCenterData);
      setCallCenters([...callCenters, response.data]);
      setNewCallCenter("");

      Swal.fire({
        icon: "success",
        title: "Success",
        text: "Call center added successfully!",
        timer: 2000,
        showConfirmButton: false,
      });
    } catch (error) {
      console.error("Error creating call center:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to create call center. Please try again.",
      });
    }
  };

  const handleDeleteCallCenter = async (callCenterId) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "This will delete the call center and all its phone numbers!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        await deleteCallCenter(callCenterId);
        setCallCenters(callCenters.filter((cc) => cc.id !== callCenterId));
        setPhoneNumbers(phoneNumbers.filter((phone) => phone.call_center !== callCenterId));

        Swal.fire({
          icon: "success",
          title: "Deleted!",
          text: "Call center has been deleted.",
          timer: 2000,
          showConfirmButton: false,
        });
      } catch (error) {
        console.error("Error deleting call center:", error);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to delete call center. Please try again.",
        });
      }
    }
  };

  // Phone Number handlers
  const handleSavePhoneNumber = async () => {
    if (!newPhoneNumber.trim()) {
      Swal.fire({
        icon: "warning",
        title: "Warning",
        text: "Please enter a phone number.",
      });
      return;
    }

    if (!selectedCallCenterForPhone) {
      Swal.fire({
        icon: "warning",
        title: "Warning",
        text: "Please select a call center.",
      });
      return;
    }

    try {
      const phoneNumberData = {
        phone_number: newPhoneNumber,
        call_center: selectedCallCenterForPhone,
        status: phoneStatus,
      };

      const response = await createPhoneNumber(phoneNumberData);
      setPhoneNumbers([...phoneNumbers, response.data]);
      setNewPhoneNumber("");
      setPhoneStatus("Available");

      Swal.fire({
        icon: "success",
        title: "Success",
        text: "Phone number added successfully!",
        timer: 2000,
        showConfirmButton: false,
      });
    } catch (error) {
      console.error("Error creating phone number:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to create phone number. Please try again.",
      });
    }
  };

  const handleDeletePhoneNumber = async (phoneNumberId) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "This will delete the phone number!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        await deletePhoneNumber(phoneNumberId);
        setPhoneNumbers(phoneNumbers.filter((phone) => phone.id !== phoneNumberId));

        Swal.fire({
          icon: "success",
          title: "Deleted!",
          text: "Phone number has been deleted.",
          timer: 2000,
          showConfirmButton: false,
        });
      } catch (error) {
        console.error("Error deleting phone number:", error);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to delete phone number. Please try again.",
        });
      }
    }
  };

  const handleSaveBrandWithPartners = async (index) => {
    try {
      // Call the parent's save handler and get the response
      const response = await onSaveBrand(index);
      
      // If brand was successfully created and we need to copy partners
      if (useExistingPartners && sourceBrandId && selectedPartners.length > 0 && response?.data?.id) {
        const newBrandId = response.data.id;
        const partnersToCopy = sourcePartners.filter((p) =>
          selectedPartners.includes(p.id)
        );

        if (partnersToCopy.length === 0) {
          return;
        }

        // Show progress
        Swal.fire({
          title: "Copying Partners",
          html: `Copying ${partnersToCopy.length} partner(s)...`,
          allowOutsideClick: false,
          didOpen: () => {
            Swal.showLoading();
          },
        });

        // Copy each partner
        let successCount = 0;
        let errorCount = 0;
        const errors = [];

        for (const partner of partnersToCopy) {
          try {
            const newPartnerData = {
              brand: newBrandId,
              name: partner.name,
              partner_type: partner.partner_type,
              email: partner.email || "",
              phone: partner.phone || "",
              address: partner.address || "",
              city: partner.city || "",
              state: partner.state || "",
              zip_code: partner.zip_code || "",
              country: partner.country || "",
              tax_number: partner.tax_number || "",
              website: partner.website || "",
              notes: partner.notes || "",
              active: partner.active !== undefined ? partner.active : true,
            };

            await createPartner(newPartnerData);
            successCount++;
          } catch (error) {
            console.error(`Error copying partner ${partner.name}:`, error);
            errorCount++;
            errors.push(`${partner.name}: ${error.response?.data?.detail || error.message}`);
          }
        }

        Swal.fire({
          icon: successCount > 0 ? "success" : "error",
          title: "Partners Copied",
          html: `<div>
            <p>Successfully copied <strong>${successCount}</strong> partner(s).</p>
            ${errorCount > 0 ? `<p>Failed to copy <strong>${errorCount}</strong> partner(s).</p>
            <details style="margin-top: 10px;">
              <summary>Error Details</summary>
              <ul style="text-align: left; margin-top: 10px;">
                ${errors.map((e) => `<li>${e}</li>`).join("")}
              </ul>
            </details>` : ""}
          </div>`,
          timer: errorCount > 0 ? 5000 : 3000,
        });
      }
    } catch (error) {
      console.error("Error in handleSaveBrandWithPartners:", error);
      // The parent's onSaveBrand will handle the error display
    }
  };

  const handleDeleteBrand = async () => {
    if (!brandData.id) {
      // If brand doesn't have an ID, just remove it from the local state
      setClientData((prevData) => {
        const updatedBrands = [...prevData.brands];
        updatedBrands.splice(index, 1);
        return { ...prevData, brands: updatedBrands };
      });
      return;
    }

    const result = await Swal.fire({
      title: "Are you sure?",
      text: `Do you want to delete the brand "${brandData.brand_name}"? This action cannot be undone.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    });

    if (result.isConfirmed) {
      try {
        await deleteBrand(brandData.id);
        Swal.fire({
          icon: "success",
          title: "Brand Deleted",
          text: `${brandData.brand_name} has been successfully deleted.`,
          timer: 3000,
          showConfirmButton: false,
        });

        // Remove brand from local state
        setClientData((prevData) => {
          const updatedBrands = [...prevData.brands];
          updatedBrands.splice(index, 1);
          return { ...prevData, brands: updatedBrands };
        });

        if (fetchClients) {
          fetchClients();
        }
      } catch (error) {
        console.error("Failed to delete brand:", error);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to delete the brand. Please try again.",
        });
      }
    }
  };

  return (
    <div className="card mt-3">
      <div className="card-body add-product pb-0">
        <div className="accordion-card-one accordion" id={`accordionExample${index}`}>
          <div className="accordion-item">
            <div className="accordion-header" id={`headingOne${index}`}>
              <div
                className="accordion-button"
                data-bs-toggle="collapse"
                data-bs-target={`#collapseOne${index}`}
                aria-controls={`collapseOne${index}`}>
                <div className="addproduct-icon">
                  <h5>
                    <Box className="add-info" />
                    <span>Brand {brandData.brand_name || index + 1}</span>
                  </h5>
                  <Link to="#">
                    <ChevronDown className="chevron-down-add" />
                  </Link>
                </div>
              </div>
            </div>
            <div
              id={`collapseOne${index}`}
              className="accordion-collapse collapse"
              aria-labelledby={`headingOne${index}`}
              data-bs-parent={`accordionExample${index}`}>
              <div className="accordion-body">
                {/* Basic Brand Information */}
                <div className="card">
                  <div className="card-body">
                    <div className="row">
                      {/* Brand Name */}
                      <div className="col-lg-6 col-sm-6 col-12">
                        <label className="form-label">Brand Name</label>
                        <input
                          type="text"
                          className="form-control"
                          value={brandData?.brand_name}
                          onChange={(e) =>
                            handleBrandFieldChange(index, "brand_name", e.target.value)
                          }
                          placeholder="Enter Brand Name"
                        />
                      </div>
                      <div className="col-lg-6 col-sm-6 col-12">
                        <label className="form-label">Brand Website</label>
                        <input
                          type="text"
                          className="form-control"
                          value={brandData.brand_website}
                          onChange={(e) =>
                            handleBrandFieldChange(index, "brand_website", e.target.value)
                          }
                          placeholder="Enter Website URL"
                        />
                      </div>
                    </div>
                    <div className="row">
                      <div className="col-lg-6 col-sm-6 col-12">
                        <label className="form-label">Brand Emails</label>
                        <div className="row">
                          {brandData.brand_emails?.map((email, emailIndex) => (
                            <div
                              key={emailIndex}
                              className="col-md-6 d-flex align-items-center mt-2">
                              <span className="me-2">{emailIndex + 1}.</span>
                              <input
                                type="email"
                                className="form-control"
                                value={email}
                                onChange={(e) =>
                                  handleBrandEmailChange(index, emailIndex, e.target.value)
                                }
                                placeholder="Enter Email"
                              />
                              <button
                                type="button"
                                className="btn btn-outline-secondary btn-sm ms-2"
                                onClick={() =>
                                  onRemoveNestedField(index, "brand_emails", emailIndex)
                                }>
                                <X size={16} />
                              </button>
                            </div>
                          ))}
                        </div>
                        <button
                          type="button"
                          className="btn btn-outline-primary mt-2"
                          onClick={() => handleAddNestedField(index, "brand_emails")}>
                          <PlusCircle className="me-2 iconsize" />
                          Add Email
                        </button>
                      </div>
                      {/* Brand Logo */}
 
                    </div>
                  </div>
                </div>

                {/* Address Information */}
                <div className="card mt-3">
                  <div className="card-body">
                    <h5>Address Information</h5>
                    <br />
                    <div className="row">
                      {/* Return Addresses */}
                      <div className="col-lg-12 col-sm-12 col-12">
                        <label className="form-label">Return Addresses</label>
                        {brandData.return_address?.map((address, addressIndex) => (
                          <div key={addressIndex} className="d-flex align-items-center mt-2">
                            <span className="me-2">{addressIndex + 1}.</span>
                            <input
                              type="text"
                              className="form-control me-2"
                              value={address}
                              onChange={(e) =>
                                handleReturnAddressChange(index, addressIndex, e.target.value)
                              }
                              placeholder="Enter Return Address"
                            />
                            <button
                              type="button"
                              className="btn btn-outline-danger btn-sm"
                              onClick={() =>
                                onRemoveNestedField(index, "return_address", addressIndex)
                              }>
                              <X size={16} />
                            </button>
                          </div>
                        ))}
                        <button
                          type="button"
                          className="btn btn-outline-primary mt-2"
                          onClick={() => handleAddNestedField(index, "return_address")}>
                          <PlusCircle className="me-2 iconsize" />
                          Add Return Address
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Logistic Information */}
                <div className="card mt-3">
                  <div className="card-body">
                    <h5>Logistic Information</h5>
                    {brandData.logistics?.map((logistic, logisticIndex) => (
                      <div className="row align-items-center mt-2" key={logisticIndex}>
                        <span className="me-2">{logisticIndex + 1}.</span>
                        <div className="col-lg-4">
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Logistic Name"
                            value={logistic.name}
                            onChange={(e) =>
                              handleLogisticFieldChange(
                                index,
                                logisticIndex,
                                "name",
                                e.target.value
                              )
                            }
                          />
                        </div>
                        <div className="col-lg-6">
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Logistic Address"
                            value={logistic.address}
                            onChange={(e) =>
                              handleLogisticFieldChange(
                                index,
                                logisticIndex,
                                "address",
                                e.target.value
                              )
                            }
                          />
                        </div>
                        <div className="col-lg-2">
                          <button
                            type="button"
                            className="btn btn-outline-danger btn-sm"
                            onClick={() => handleRemoveLogisticInfo(index, logisticIndex)}>
                            <X size={16} />
                          </button>
                        </div>
                      </div>
                    ))}
                    <button
                      className="btn btn-outline-primary mt-3"
                      onClick={() => handleAddLogisticInfo(index)}>
                      <PlusCircle className="me-2 iconsize" />
                      Add Logistic Information
                    </button>
                  </div>
                </div>

                {/* Payment Information */}
                <div className="card mt-3">
                  <div className="card-body">
                    <h5>Payment Information</h5>
                    <br />

                    {/* Banks */}
                    <div className="row">
                      <div className="col-lg-12 col-sm-12 col-12">
                        <label className="form-label">Banks</label>
                        <div className="row">
                          {brandData.banks?.map((bank, bankIndex) => (
                            <div
                              key={bankIndex}
                              className="col-md-6 d-flex align-items-center mt-2">
                              <span className="me-2">{bankIndex + 1}.</span>
                              <input
                                type="text"
                                className="form-control"
                                value={bank}
                                onChange={(e) => handleBankChange(index, bankIndex, e.target.value)}
                                placeholder="Enter Bank Name"
                              />
                              <button
                                type="button"
                                className="btn btn-outline-secondary btn-sm ms-2"
                                onClick={() => onRemoveNestedField(index, "banks", bankIndex)}>
                                <X size={16} />
                              </button>
                            </div>
                          ))}
                        </div>
                        <button
                          type="button"
                          className="btn btn-outline-primary mt-2"
                          onClick={() => handleAddNestedField(index, "banks")}>
                          <PlusCircle className="me-2 iconsize" />
                          Add Bank
                        </button>
                      </div>
                    </div>

                    {/* Payee Names */}
                    <div className="row mt-3">
                      <div className="col-lg-12 col-sm-12 col-12">
                        <label className="form-label">Payee Names</label>
                        <div className="row">
                          {brandData.payee_names?.map((payee, payeeIndex) => (
                            <div
                              key={payeeIndex}
                              className="col-md-6 d-flex align-items-center mt-2">
                              <span className="me-2">{payeeIndex + 1}.</span>
                              <input
                                type="text"
                                className="form-control"
                                value={payee}
                                onChange={(e) =>
                                  handlePayeeNameChange(index, payeeIndex, e.target.value)
                                }
                                placeholder="Enter Payee Name"
                              />
                              <button
                                type="button"
                                className="btn btn-outline-secondary btn-sm ms-2"
                                onClick={() =>
                                  onRemoveNestedField(index, "payee_names", payeeIndex)
                                }>
                                <X size={16} />
                              </button>
                            </div>
                          ))}
                        </div>
                        <button
                          type="button"
                          className="btn btn-outline-primary mt-2"
                          onClick={() => handleAddNestedField(index, "payee_names")}>
                          <PlusCircle className="me-2 iconsize" />
                          Add Payee Name
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Merchant Accounts Information */}
                <div className="card mt-3">
                  <div className="card-body">
                    <h5>Merchant Accounts</h5>
                    <br />
                    <div className="table-responsive">
                      <table className="table table-bordered">
                        <thead>
                          <tr>
                            <th>Merchant Account</th>
                            <th>Gateway Name</th>
                            <th>Gateway Link</th>
                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {brandData.merchant_accounts?.map((account, accountIndex) => (
                            <tr key={accountIndex}>
                              <td>
                                <input
                                  type="text"
                                  className="form-control"
                                  value={account.account}
                                  onChange={(e) =>
                                    handleMerchantAccountFieldChange(
                                      index,
                                      accountIndex,
                                      "account",
                                      e.target.value
                                    )
                                  }
                                  placeholder="Enter Merchant Account"
                                />
                              </td>
                              <td colSpan="2">
                                <div className="gateway-info">
                                  {account.gateways?.map((gateway, gatewayIndex) => (
                                    <div
                                      key={gatewayIndex}
                                      className="d-flex align-items-center mt-2">
                                      <input
                                        type="text"
                                        className="form-control me-2"
                                        value={gateway.name}
                                        onChange={(e) =>
                                          handleGatewayFieldChange(
                                            index,
                                            accountIndex,
                                            gatewayIndex,
                                            "name",
                                            e.target.value
                                          )
                                        }
                                        placeholder="Gateway Name"
                                      />
                                      <input
                                        type="url"
                                        className="form-control me-2"
                                        value={gateway.link}
                                        onChange={(e) =>
                                          handleGatewayFieldChange(
                                            index,
                                            accountIndex,
                                            gatewayIndex,
                                            "link",
                                            e.target.value
                                          )
                                        }
                                        placeholder="Gateway Link"
                                      />
                                      <button
                                        type="button"
                                        className="btn btn-outline-secondary btn-sm"
                                        onClick={() =>
                                          handleRemoveGateway(index, accountIndex, gatewayIndex)
                                        }>
                                        <X size={16} />
                                      </button>
                                    </div>
                                  ))}
                                  <button
                                    type="button"
                                    className="btn btn-outline-primary mt-2"
                                    onClick={() => handleAddGateway(index, accountIndex)}>
                                    <PlusCircle className="me-2 iconsize" />
                                    Add Gateway
                                  </button>
                                </div>
                              </td>
                              <td>
                                <button
                                  type="button"
                                  className="btn btn-outline-danger btn-sm"
                                  onClick={() => handleRemoveMerchantAccount(index, accountIndex)}>
                                  <X size={16} />
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <button
                      type="button"
                      className="btn btn-outline-primary mt-2"
                      onClick={() => handleAddMerchantAccount(index)}>
                      <PlusCircle className="me-2 iconsize" />
                      Add Merchant Account
                    </button>
                  </div>
                </div>

                {/* Call Centers Information */}
                <div className="card mt-3">
                  <div className="card-body">
                    <h5>Call Centers</h5>
                    <br />

                    {/* Add new call center */}
                    <div className="row mb-3">
                      <div className="col-md-8">
                        <input
                          type="text"
                          className="form-control"
                          value={newCallCenter}
                          onChange={(e) => setNewCallCenter(e.target.value)}
                          placeholder="Enter Call Center Name"
                        />
                      </div>
                      <div className="col-md-4">
                        <button
                          type="button"
                          className="btn btn-primary"
                          onClick={handleSaveCallCenter}
                          disabled={!brandData?.id}>
                          <PlusCircle className="me-2 iconsize" />
                          Save Call Center
                        </button>
                      </div>
                    </div>

                    {/* List existing call centers */}
                    <div className="table-responsive">
                      <table className="table table-bordered">
                        <thead>
                          <tr>
                            <th>Call Center Name</th>
                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {callCenters.map((callCenter) => (
                            <tr key={callCenter.id}>
                              <td>{callCenter.name}</td>
                              <td>
                                <button
                                  type="button"
                                  className="btn btn-outline-danger btn-sm"
                                  onClick={() => handleDeleteCallCenter(callCenter.id)}>
                                  <X size={16} />
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {!brandData?.id && (
                      <div className="alert alert-info mt-2">
                        Please save the brand first to add call centers.
                      </div>
                    )}
                  </div>
                </div>

                {/* Phone Numbers Information */}
                <div className="card mt-3">
                  <div className="card-body">
                    <h5>Phone Numbers</h5>
                    <br />

                    {/* Add new phone number */}
                    <div className="row mb-3">
                      <div className="col-md-3">
                        <select
                          className="form-control"
                          value={selectedCallCenterForPhone}
                          onChange={(e) => setSelectedCallCenterForPhone(e.target.value)}>
                          <option value="">Select Call Center</option>
                          {callCenters.map((callCenter) => (
                            <option key={callCenter.id} value={callCenter.id}>
                              {callCenter.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="col-md-3">
                        <input
                          type="text"
                          className="form-control"
                          value={newPhoneNumber}
                          onChange={(e) => setNewPhoneNumber(e.target.value)}
                          placeholder="Enter Phone Number"
                        />
                      </div>
                      <div className="col-md-3">
                        <input
                          type="text"
                          className="form-control"
                          value={phoneStatus}
                          onChange={(e) => setPhoneStatus(e.target.value)}
                          placeholder="Status"
                        />
                      </div>
                      <div className="col-md-3">
                        <button
                          type="button"
                          className="btn btn-primary"
                          onClick={handleSavePhoneNumber}
                          disabled={!brandData?.id}>
                          <PlusCircle className="me-2 iconsize" />
                          Save Phone
                        </button>
                      </div>
                    </div>

                    {/* List existing phone numbers */}
                    <div className="table-responsive">
                      <table className="table table-bordered">
                        <thead>
                          <tr>
                            <th>Phone Number</th>
                            <th>Call Center</th>
                            <th>Status</th>
                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {phoneNumbers.map((phoneNumber) => {
                            const callCenter = callCenters.find(
                              (cc) => cc.id === phoneNumber.call_center
                            );
                            return (
                              <tr key={phoneNumber.id}>
                                <td>{phoneNumber.phone_number}</td>
                                <td>{callCenter ? callCenter.name : "Unknown"}</td>
                                <td>
                                  <span className="badge bg-success">{phoneNumber.status}</span>
                                </td>
                                <td>
                                  <button
                                    type="button"
                                    className="btn btn-outline-danger btn-sm"
                                    onClick={() => handleDeletePhoneNumber(phoneNumber.id)}>
                                    <X size={16} />
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>

                    {!brandData?.id && (
                      <div className="alert alert-info mt-2">
                        Please save the brand first to add phone numbers.
                      </div>
                    )}

                    {callCenters.length === 0 && brandData?.id && (
                      <div className="alert alert-warning mt-2">
                        Please add a call center first to add phone numbers.
                      </div>
                    )}
                  </div>
                </div>

                {/* Partner Copying Section - Only show for new brands (not yet saved) */}
                {!brandData?.id && (
                  <div className="card mt-3">
                    <div className="card-body">
                      <h5>Vendor/Customer List</h5>
                      <br />
                      <div className="form-check mb-3">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id={`useExistingPartners${index}`}
                          checked={useExistingPartners}
                          onChange={(e) => {
                            setUseExistingPartners(e.target.checked);
                            if (!e.target.checked) {
                              setSourceBrandId("");
                              setSourcePartners([]);
                              setSelectedPartners([]);
                            }
                          }}
                        />
                        <label className="form-check-label" htmlFor={`useExistingPartners${index}`}>
                          Use existing vendor/customer list from another brand
                        </label>
                      </div>

                      {useExistingPartners && (
                        <div className="mt-3">
                          <div className="row mb-3">
                            <div className="col-md-12">
                              <label className="form-label">Select Brand to Copy From</label>
                              <select
                                className="form-control"
                                value={sourceBrandId}
                                onChange={(e) => setSourceBrandId(e.target.value)}
                                disabled={loadingPartners}>
                                <option value="">-- Select a Brand --</option>
                                {availableBrands.map((brand) => (
                                  <option key={brand.id} value={brand.id}>
                                    {brand.brand_name}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>

                          {sourceBrandId && (
                            <div className="mt-3">
                              {loadingPartners ? (
                                <div className="text-center">
                                  <div className="spinner-border spinner-border-sm" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                  </div>
                                  <p className="mt-2">Loading partners...</p>
                                </div>
                              ) : sourcePartners.length > 0 ? (
                                <div>
                                  <div className="d-flex justify-content-between align-items-center mb-3">
                                    <label className="form-label mb-0">
                                      Select Partners to Copy ({selectedPartners.length} of {sourcePartners.length} selected)
                                    </label>
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-outline-primary"
                                      onClick={handleSelectAllPartners}>
                                      {selectedPartners.length === sourcePartners.length
                                        ? "Deselect All"
                                        : "Select All"}
                                    </button>
                                  </div>
                                  <div
                                    className="border rounded p-3"
                                    style={{ maxHeight: "300px", overflowY: "auto" }}>
                                    {sourcePartners.map((partner) => (
                                      <div key={partner.id} className="form-check mb-2">
                                        <input
                                          className="form-check-input"
                                          type="checkbox"
                                          id={`partner-${partner.id}`}
                                          checked={selectedPartners.includes(partner.id)}
                                          onChange={() => handlePartnerToggle(partner.id)}
                                        />
                                        <label
                                          className="form-check-label"
                                          htmlFor={`partner-${partner.id}`}>
                                          <strong>{partner.name}</strong> (
                                          {partner.partner_type === "customer"
                                            ? "Customer"
                                            : partner.partner_type === "vendor"
                                            ? "Vendor"
                                            : "Both"}
                                          )
                                          {partner.email && ` - ${partner.email}`}
                                        </label>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              ) : (
                                <div className="alert alert-info">
                                  No partners found for the selected brand.
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="d-flex justify-content-between mt-3">
                  <div>
                    <button type="button" className="btn btn-danger" onClick={handleDeleteBrand}>
                      <Trash2 className="me-2" size={16} />
                      Delete Brand
                    </button>
                  </div>
                  <div>
                    <button
                      type="button"
                      className="btn btn-secondary me-2"
                      onClick={() => onCancelBrand(index)}>
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary"
                      onClick={async (e) => {
                        e.preventDefault();
                        await handleSaveBrandWithPartners(index);
                      }}>
                      Save Brand
                    </button>
                  </div>
                </div>
                <br />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BrandForm;
